<!-- @format -->

<template>
	<div class="contract-residual-values-table-modal">
		<div class="bg-primary-light mb-6 px-10 py-3 rounded">
			<CoreTwoColumnsRow>
				<template #left
					><span>{{ $t('§caf.list_price_including_extras') }}</span></template
				>
				<template #right
					><span class="text-bold">{{
						displayAsCurrency(residualValueBaseAmount, RV_CURRENCY_DIGITS, currencyCode)
					}}</span></template
				>
			</CoreTwoColumnsRow>
			<CoreTwoColumnsRow>
				<template #left
					><span>{{ $t('§contracts.depreciation_period') }}</span></template
				>
				<template #right>
					<CoreDropdown
						v-model="depreciationPeriodYears"
						:options="depreciationPeriodOptions"
						:sort="false"
						:width="140"
						:disabled="
							isReadonly ||
							isBusy ||
							saving ||
							pendingCopy ||
							pasteOpen ||
							hasCellValidationErrors
						"
						@update:modelValue="onDepreciationPeriodChange"
					/>
				</template>
			</CoreTwoColumnsRow>
		</div>

		<div
			v-if="pendingCopy"
			class="bg-warning-light mb-6 px-10 py-3 rounded flex flex-col justify-start gap-2"
		>
			<span>{{ $t('§contracts.copy_equipment_type_rvt_overwrites_current') }}</span>
			<div class="flex gap-2">
				<CoreButton
					label="§ui_button.proceed"
					variant="primary"
					icon="pi-copy"
					@click="applyCopy"
				/>
				<CoreButton
					label="§ui_button.keep_current_values"
					variant="tertiary"
					icon="pi-times"
					@click="cancelCopy"
				/>
			</div>
		</div>
		<div
			v-else-if="pendingPeriod !== null"
			class="bg-warning-light mb-6 px-10 py-3 rounded flex flex-col justify-start gap-2"
		>
			<span>{{ $t(pendingPeriodMessage) }}</span>
			<div class="flex gap-2">
				<CoreButton
					label="§ui_button.proceed"
					variant="primary"
					icon="pi-refresh"
					@click="confirmPeriodChange"
				/>
				<CoreButton
					label="§ui_button.keep_current_values"
					variant="tertiary"
					icon="pi-times"
					@click="cancelPeriodChange"
				/>
			</div>
		</div>

		<CoreButton
			v-if="!isReadonly"
			class="contract-residual-values-table-modal__paste-button mb-6"
			label="§ui_button.paste_values"
			variant="secondary"
			icon="pi-clipboard"
			:disabled="tenorControlsDisabled"
			@click="openPaste"
		/>

		<div
			v-if="pasteOpen"
			class="contract-residual-values-table-modal__paste bg-primary-light mb-6 px-10 py-3 rounded flex flex-col gap-2"
		>
			<span>{{ $t('§contracts.rv_paste_hint', { currency: currencySymbol }) }}</span>
			<textarea
				v-model="pasteText"
				class="contract-residual-values-table-modal__paste-input"
				rows="8"
			/>
			<span
				v-if="pasteError"
				class="contract-residual-values-table-modal__paste-error"
			>
				{{ $t(pasteError.message, pasteError.params) }}
			</span>
			<div class="flex gap-2">
				<CoreButton
					label="§ui_button.confirm"
					variant="primary"
					icon="pi-check"
					:disabled="!pasteText.trim()"
					@click="applyPaste"
				/>
				<CoreButton
					label="§ui_button.cancel"
					variant="tertiary"
					icon="pi-times"
					@click="cancelPaste"
				/>
			</div>
		</div>

		<DataTable
			:value="table"
			:editMode="isReadonly || isBusy || pasteOpen ? undefined : 'cell'"
			:loading="isBusy"
			@cell-edit-init="onCellEditInit"
			@cell-edit-complete="onCellEditComplete"
			@cell-edit-cancel="onCellEditCancel"
			class="residual-values-table rv-matrix rv-matrix--single"
			:class="{ 'filter-blur': isBusy }"
			responsiveLayout="scroll"
		>
			<Column
				field="months"
				headerClass="rv-mh"
				bodyClass="rv-mcol"
			>
				<template #header>
					<span class="font-bold">
						{{ $t('§contracts.rv_tenor_months') }}
					</span>
				</template>
				<template #body="slotProps">
					<span>
						{{
							slotProps.data.months === 1
								? $t('§eq_type.day_1')
								: slotProps.data.months
						}}
					</span>
				</template>
			</Column>

			<template
				v-for="key in displayedUtilizationKeys"
				:key="key"
			>
				<Column
					:field="key"
					:pt="{
						headerCell: { class: 'rv-gh', 'data-g': 0 },
						bodyCell: { class: 'rv-gcol', 'data-g': 0 },
					}"
				>
					<template #header>
						<span class="rv-gh__label rv-gh__label--split">
							<span class="font-bold">{{
								$t('§contracts.rv_value_in_currency', {
									currency_symbol: currencySymbol,
								})
							}}</span>
							<span class="font-bold">{{
								$t('§contracts.rv_value_percent_of_list_price')
							}}</span>
						</span>
					</template>
					<template #body="slotProps">
						<span
							class="contract-residual-values-table-modal__cell"
							:class="{
								'contract-residual-values-table-modal__cell--error':
									cellValidationErrors[cellKey(slotProps.data.months, key)],
							}"
							:title="cellErrorTitle(slotProps.data.months, key)"
						>
							<CoreCurrencyPercentageInput
								:modelValue="slotProps.data[key]"
								:baseValue="Number(residualValueBaseAmount) || 0"
								:currency="currencyCode"
								:currencyDigits="RV_CURRENCY_DIGITS"
								:percentageDigits="1"
								displayOnly
							/>
						</span>
					</template>
					<template #editor="slotProps">
						<CoreCurrencyPercentageInput
							v-model="slotProps.data[key]"
							class="modal-residual-values-table__editor"
							:baseValue="Number(residualValueBaseAmount) || 0"
							:currency="currencyCode"
							:currencyDigits="RV_CURRENCY_DIGITS"
							:percentageDigits="1"
							:showBorder="false"
							autofocus
							:autofocusSide="editFocusSide"
							:validator="getCellValidator(slotProps.data.months, key)"
							:customNotificationMessage="
								getCellValidationMessage(slotProps.data.months, key)
							"
						/>
					</template>
				</Column>
			</template>

			<Column
				v-if="!isReadonly"
				headerClass="rv-tenor-h"
				bodyClass="rv-tenor-col"
			>
				<template #header>
					<span class="font-bold">{{ $t('§contracts.is_rv_tenor') }}</span>
				</template>
				<template #body="slotProps">
					<RadioButton
						v-if="canPickTenor(slotProps.data.months)"
						:modelValue="tenorMonths"
						:value="slotProps.data.months"
						name="rv-tenor"
						:disabled="tenorControlsDisabled"
						@click="toggleTenor(slotProps.data.months)"
					/>
				</template>
			</Column>
		</DataTable>

		<p class="contract-residual-values-table-modal__note">
			{{
				$t('§contracts.rv_full_amortisation_note', {
					amount: displayAsCurrency(1, 0, currencyCode),
					month: periodEndMonths,
				})
			}}
		</p>

		<div
			v-if="pasteSummary.length"
			class="bg-primary-light mt-6 px-10 py-3 rounded flex flex-col"
		>
			<span
				v-for="line in pasteSummary"
				:key="line.message"
				>{{ $t(line.message, line.params) }}</span
			>
		</div>

		<div
			v-if="tenorBelowNext"
			class="bg-warning-light mt-6 px-10 py-3 rounded"
		>
			<span>{{ $t('§contracts.rv_tenor_below_next_row') }}</span>
		</div>

		<div class="flex flex-wrap justify-start gap-4 mt-8">
			<CoreButton
				v-if="!isReadonly"
				label="§ui_button.save"
				variant="primary"
				icon="pi-save"
				:disabled="
					saving ||
					isBusy ||
					hasCellValidationErrors ||
					pendingCopy ||
					pendingPeriod !== null ||
					pasteOpen
				"
				:loading="saving"
				@click="handleSave"
			/>
			<CoreButton
				v-if="canCopyUnitTypeTable"
				label="§ui_button.copy_equipment_type_table"
				variant="secondary"
				icon="pi-copy"
				:disabled="
					saving ||
					isBusy ||
					pendingCopy ||
					pendingPeriod !== null ||
					tableMatchesUnitTypeCopy ||
					pasteOpen
				"
				@click="onCopyUnitTypeTable"
			/>
			<CoreButton
				v-if="!isReadonly"
				label="§ui_button.calculate_depreciation_curve"
				variant="secondary"
				icon="pi-chart-line"
				:disabled="!canCalculateCurve"
				@click="calculateCurve"
			/>
			<CoreButton
				label="§ui_button.cancel"
				variant="tertiary"
				icon="pi-times"
				:disabled="saving"
				@click="closeModal"
			/>
		</div>
	</div>
</template>

<script>
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import RadioButton from 'primevue/radiobutton'
import CoreCurrencyPercentageInput from '@/components/core/CoreCurrencyPercentageInput.vue'
import CoreButton from '@/components/core/CoreButton.vue'
import { useModalStore } from '@/stores/modal'
import { useContractStore } from '@/stores/contract'
import ApiService from '@/api/ApiService'
import CoreTwoColumnsRow from '@/components/core/CoreTwoColumnsRow.vue'
import CoreDropdown from '@/components/core/CoreDropdown.vue'
import {
	DEFAULT_RV_DEPRECIATION_PERIOD_YEARS,
	RV_CURRENCY_DIGITS,
	RV_RESIDUAL_FLOOR,
} from '@/data/constants.js'
import { rvDepreciationPeriodOptions } from '@/helpers/rvDepreciationPeriodOptions.js'
import { roundHalfUp } from '@/helpers/roundHalfUp.js'
import { parseRvPaste, fillRvTable } from '@/helpers/parseRvPaste.js'

const RECALCULATION_HEAD_MONTHS = [0, 1, 12]
const COPY_MATCH_TOLERANCE_EUROS = 1

export default {
	name: 'ContractResidualValuesTableModal',

	components: {
		DataTable,
		Column,
		RadioButton,
		CoreCurrencyPercentageInput,
		CoreButton,
		CoreTwoColumnsRow,
		CoreDropdown,
	},

	data() {
		return {
			table: [],
			loading: false,
			saving: false,
			residualValueBaseAmount: 0,
			currencyCodeFromApi: null,
			depreciationPeriodYears: DEFAULT_RV_DEPRECIATION_PERIOD_YEARS,
			lastAppliedPeriod: DEFAULT_RV_DEPRECIATION_PERIOD_YEARS,
			recalculating: false,
			editFocusSide: null,
			cellValidationErrors: {},
			unitTypeCopy: null,
			baselineTable: null,
			pendingCopy: false,
			pendingPeriod: null,
			tenorMonths: null,
			pasteOpen: false,
			pasteText: '',
			pasteError: null,
			pasteSummary: [],
			// per cell (cellKey), since a refused edit can leave one editor open while Tab opens the next
			editStartedRed: {},
			RV_CURRENCY_DIGITS: RV_CURRENCY_DIGITS,
		}
	},

	created() {
		this.modalStore = useModalStore()
		this.contractStore = useContractStore()
	},

	computed: {
		isReadonly() {
			const data = this.contractStore.contractData
			return !!data?.approved || !this.hasPermission('manage contracts')
		},

		isBusy() {
			return this.loading || this.recalculating
		},

		displayedUtilizationKeys() {
			return ['100%']
		},

		contractId() {
			return this.modalStore.data?.contractId
		},

		lesseeBuyBackObligation() {
			return this.modalStore.data?.lesseeBuyBackObligation
		},

		currencyCode() {
			return (
				this.currencyCodeFromApi || this.contractStore.contractData?.currency_code || 'EUR'
			)
		},

		depreciationPeriodOptions() {
			return rvDepreciationPeriodOptions(this.$t)
		},

		hasCellValidationErrors() {
			return Object.keys(this.cellValidationErrors).length > 0
		},

		canCopyUnitTypeTable() {
			return !this.isReadonly && this.unitTypeCopy?.rows.length > 0
		},

		editedSinceBaseline() {
			return this.baselineTable !== null && this.serializeTable() !== this.baselineTable
		},

		currencySymbol() {
			return (
				new Intl.NumberFormat(this.numberFormat(), {
					style: 'currency',
					currency: this.currencyCode,
				})
					.formatToParts(0)
					.find((part) => part.type === 'currency')?.value ?? this.currencyCode
			)
		},

		periodEndMonths() {
			return Number(this.lastAppliedPeriod) * 12
		},

		tenorControlsDisabled() {
			return (
				this.isBusy ||
				this.saving ||
				this.pendingCopy ||
				this.pendingPeriod !== null ||
				this.pasteOpen
			)
		},

		tenorBelowNext() {
			if (this.tenorMonths === null) {
				return false
			}

			const index = this.table.findIndex((row) => row.months === this.tenorMonths)
			const next = this.table[index + 1]
			return !!next && Number(next['100%']) > this.valueAtMonth(this.tenorMonths)
		},

		canCalculateCurve() {
			return (
				this.tenorMonths !== null &&
				this.tenorMonths > 12 &&
				!this.tenorBelowNext &&
				!this.tenorControlsDisabled &&
				!this.hasCellValidationErrors
			)
		},

		pendingPeriodMessage() {
			if (this.tenorMonths === null) {
				return '§contracts.recalculate_rv_table_discards_manual_changes'
			}

			return this.tenorFits(this.pendingPeriod)
				? '§contracts.recalculate_rv_rows_after_tenor_discards_manual_changes'
				: '§contracts.rv_tenor_beyond_period_unselects_it'
		},

		tableMatchesUnitTypeCopy() {
			if (
				!this.unitTypeCopy ||
				Number(this.lastAppliedPeriod) !== Number(this.unitTypeCopy.period)
			) {
				return false
			}

			const copy = this.unitTypeCopy.rows
			return (
				this.table.length === copy.length &&
				this.table.every(
					(row, i) =>
						row.months === copy[i].months &&
						Math.abs(Number(row['100%']) - Number(copy[i]['100%'])) <=
							COPY_MATCH_TOLERANCE_EUROS,
				)
			)
		},
	},

	mounted() {
		this.fetchData()
	},

	methods: {
		closeModal() {
			this.modalStore.setClose()
		},

		cellKey(months, field) {
			return `${months}-${field}`
		},

		cellErrorTitle(months, field) {
			const error = this.cellValidationErrors[this.cellKey(months, field)]
			return error ? this.$t(error.message) : null
		},

		openPaste() {
			this.pasteText = ''
			this.pasteError = null
			this.pasteSummary = []
			this.pasteOpen = true
		},

		cancelPaste() {
			this.pasteOpen = false
			this.pasteError = null
		},

		applyPaste() {
			const base = Number(this.residualValueBaseAmount)
			const parsed = parseRvPaste(this.pasteText, this.numberFormat(), this.currencyCode, base)
			const filled = parsed.error ? parsed : fillRvTable(this.table, parsed.points, base)
			if (filled.error) {
				this.pasteError = filled.error
				return
			}

			this.pasteSummary = this.pasteSummaryLines(parsed.inYears, filled.read, this.tenorMonths !== null)
			this.table = filled.rows
			this.tenorMonths = null
			this.cellValidationErrors = this.validateTable()
			this.pasteOpen = false
		},

		pasteSummaryLines(inYears, read, tenorWasPicked) {
			const lines = [
				inYears
					? {
							message: '§contracts.rv_paste_read_years',
							params: { count: read.count, from: read.first / 12, to: read.last / 12 },
						}
					: {
							message: '§contracts.rv_paste_read_months',
							params: { count: read.count, from: read.first, to: read.last },
						},
			]
			if (read.beyondTable) {
				lines.push({ message: '§contracts.rv_paste_beyond_table', params: { month: read.lastRow } })
			}
			if (tenorWasPicked) {
				lines.push({ message: '§contracts.rv_paste_tenor_unselected', params: {} })
			}
			return lines
		},

		cellError(rowIndex, field, value) {
			const row = this.table[rowIndex]
			const error = (message) => ({ severity: 'error', message })

			if (Number.isNaN(value) || value < RV_RESIDUAL_FLOOR) {
				return error('§ui.residual_value_must_be_at_least_one')
			}
			if (rowIndex === 0 && value > Number(this.residualValueBaseAmount)) {
				return error('§ui.residual_value_exceeds_contract_base')
			}
			if (rowIndex > 0 && value > Number(this.table[rowIndex - 1][field])) {
				return error(
					row.months === this.tenorMonths
						? '§ui.rv_tenor_exceeds_previous'
						: '§ui.residual_value_exceeds_previous',
				)
			}
			if (
				RECALCULATION_HEAD_MONTHS.slice(0, -1).includes(row.months) &&
				rowIndex < this.table.length - 1 &&
				value < Number(this.table[rowIndex + 1][field])
			) {
				return error('§ui.residual_value_below_next')
			}
			return null
		},

		validateTable() {
			return Object.fromEntries(
				this.table
					.map((row, i) => [
						this.cellKey(row.months, '100%'),
						this.cellError(i, '100%', Number(row['100%'])),
					])
					.filter(([, error]) => error),
			)
		},

		recheckCellErrors() {
			const current = this.validateTable()
			this.cellValidationErrors = Object.fromEntries(
				Object.keys(this.cellValidationErrors)
					.filter((key) => current[key])
					.map((key) => [key, current[key]]),
			)
		},

		getTableData(rvt) {
			const utilizationKeys = Object.keys(rvt)
			const months = [
				...new Set(
					utilizationKeys.flatMap((utilization) =>
						Object.keys(rvt[utilization]).map(Number),
					),
				),
			].sort((a, b) => a - b)

			return months.map((month) => {
				const row = { months: month }
				utilizationKeys.forEach((utilization) => {
					row[`${Math.round(Number(utilization.replace('-', '.')) * 100)}%`] = roundHalfUp(
						Number(rvt[utilization][month] ?? 0),
						RV_CURRENCY_DIGITS,
					)
				})
				return row
			})
		},

		filterToFullUtilization(rvt) {
			const fullUtilizationKey = Object.keys(rvt || {}).find(
				(key) => Number(key.replace('-', '.')) === 1,
			)
			return fullUtilizationKey ? { [fullUtilizationKey]: rvt[fullUtilizationKey] } : rvt
		},

		async fetchData() {
			this.loading = true
			try {
				const response = await ApiService.get('contract.residual_values_table.show', {
					contract: this.contractId,
				})
				const data = response.data
				const copy = data.unit_type_copy
				this.unitTypeCopy = copy
					? {
							period:
								Number(copy.depreciation_period_years) ||
								DEFAULT_RV_DEPRECIATION_PERIOD_YEARS,
							rows: this.getTableData(
								this.filterToFullUtilization(copy.residual_values_table),
							),
						}
					: null
				this.pendingPeriod = null
				this.residualValueBaseAmount = data.residual_value_base_amount ?? 0
				this.currencyCodeFromApi = data.currency_code || null
				this.depreciationPeriodYears =
					Number(data.depreciation_period_years) || DEFAULT_RV_DEPRECIATION_PERIOD_YEARS
				this.lastAppliedPeriod = this.depreciationPeriodYears
				this.table = this.getTableData(
					this.filterToFullUtilization(data.residual_values_table),
				)
				this.baselineTable = this.serializeTable()
			} catch (error) {
				console.error('Error fetching residual values table:', error)
				this.closeModal()
			} finally {
				this.loading = false
			}
		},

		transformTableForBackend() {
			const rvt = {}
			const keysToSave = ['100%']

			this.table.forEach((row) => {
				keysToSave.forEach((key) => {
					const numberKey = Number(key.slice(0, -1)) / 100
					const laravelFriendlyKey = numberKey.toString().replace('.', '-')

					if (rvt[laravelFriendlyKey] === undefined) {
						rvt[laravelFriendlyKey] = {}
					}
					rvt[laravelFriendlyKey][row.months] = row[key]
				})
			})

			return rvt
		},

		async handleSave() {
			this.saving = true
			try {
				const payload = {
					residual_values_table: this.transformTableForBackend(),
					depreciation_period_years: this.depreciationPeriodYears,
				}
				if (this.lesseeBuyBackObligation !== undefined) {
					payload.lessee_buy_back_obligation = this.lesseeBuyBackObligation
				}

				await ApiService.post('contract.residual_values_table.store', payload, {
					contract: this.contractId,
				})

				this.contractStore.notification('success', '§ui_message.update_successful')

				await this.contractStore.getContractData({ contract: this.contractId })

				this.closeModal()
			} catch (error) {
				console.error('Error saving residual values table:', error)
				this.contractStore.notification('error', '§ui_message.update_failed')
			} finally {
				this.saving = false
			}
		},

		valueAtMonth(months) {
			const row = this.table.find((entry) => entry.months === months)
			return Number(row?.['100%'] ?? 0)
		},

		headPayload() {
			return Object.fromEntries(
				RECALCULATION_HEAD_MONTHS.map((months) => [months, this.valueAtMonth(months)]),
			)
		},

		serializeTable() {
			return JSON.stringify(this.table.map((row) => [row.months, Number(row['100%'])]))
		},

		onCopyUnitTypeTable() {
			if (this.editedSinceBaseline) {
				this.pendingCopy = true
				return
			}

			this.applyCopy()
		},

		applyCopy() {
			const rows = (this.unitTypeCopy?.rows || []).filter((row) =>
				Number.isFinite(row['100%']),
			)

			if (rows.length === 0) {
				this.pendingCopy = false
				return
			}

			this.table = rows.map((row) => ({ ...row }))
			this.depreciationPeriodYears = this.unitTypeCopy.period
			this.lastAppliedPeriod = this.unitTypeCopy.period
			this.tenorMonths = null
			this.cellValidationErrors = {}
			this.pasteSummary = []
			this.baselineTable = this.serializeTable()
			this.pendingCopy = false
		},

		cancelCopy() {
			this.pendingCopy = false
		},

		async onDepreciationPeriodChange(newValue) {
			const period = Number(newValue)
			if (period === this.lastAppliedPeriod) {
				this.pendingPeriod = null
				return
			}

			const unpicksTenor = this.tenorMonths !== null && !this.tenorFits(period)
			if (unpicksTenor || this.editedSinceBaseline) {
				this.pendingPeriod = period
				return
			}

			await this.recalculate(period)
		},

		confirmPeriodChange() {
			const period = this.pendingPeriod
			this.pendingPeriod = null
			return this.recalculate(period)
		},

		cancelPeriodChange() {
			this.pendingPeriod = null
			this.depreciationPeriodYears = this.lastAppliedPeriod
		},

		async requestFormulaRows(period, tenor) {
			const payload = {
				depreciation_period_years: period,
				head: this.headPayload(),
			}
			if (tenor !== null) {
				payload.tenor = { months: tenor, value: this.valueAtMonth(tenor) }
			}

			const response = await ApiService.post(
				'contract.residual_values_table.calculate',
				payload,
				{ contract: this.contractId },
			)

			return this.getTableData(
				this.filterToFullUtilization(response.data.residual_values_table),
			)
		},

		mergeRows(fresh, inRange) {
			this.table = this.table.map((row) => {
				const match =
					inRange(row.months) && fresh.find((entry) => entry.months === row.months)
				return match ? { ...row, '100%': match['100%'] } : row
			})
		},

		async calculateCurve() {
			const tenor = this.tenorMonths
			this.recalculating = true
			try {
				const rows = await this.requestFormulaRows(this.lastAppliedPeriod, tenor)
				this.mergeRows(rows, (months) => months > 1 && months < tenor)
			} catch (error) {
				console.error('Error calculating the depreciation curve:', error)
				this.contractStore.notification('error', '§ui_message.update_failed')
			} finally {
				this.recalculating = false
			}
		},

		async recalculate(period) {
			const tenor = this.tenorFits(period) ? this.tenorMonths : null
			const keepsEdits = tenor !== null && this.editedSinceBaseline
			this.recalculating = true
			try {
				const rows = await this.requestFormulaRows(period, tenor)

				this.table =
					tenor === null
						? rows
						: rows.map((row) =>
								row.months <= tenor
									? { ...row, '100%': this.valueAtMonth(row.months) }
									: row,
							)
				this.tenorMonths = tenor
				this.cellValidationErrors = {}
				this.pasteSummary = []
				this.lastAppliedPeriod = period
				if (!keepsEdits) {
					this.baselineTable = this.serializeTable()
				}
			} catch (error) {
				console.error('Error recalculating residual values table:', error)
				this.depreciationPeriodYears = this.lastAppliedPeriod
				this.contractStore.notification('error', '§ui_message.update_failed')
			} finally {
				this.recalculating = false
			}
		},

		canPickTenor(months) {
			return months >= 12 && months < this.periodEndMonths
		},

		toggleTenor(months) {
			this.tenorMonths = this.tenorMonths === months ? null : months
			this.pasteSummary = []
		},

		tenorFits(period) {
			return this.tenorMonths !== null && this.tenorMonths < Number(period) * 12
		},

		onCellEditInit(event) {
			const target = event.originalEvent?.target
			this.editFocusSide = target?.closest?.('.core-currency-percentage-input__percentage')
				? 'percentage'
				: 'currency'
			const key = this.cellKey(event.data.months, event.field)
			this.editStartedRed[key] = !!this.cellValidationErrors[key]
		},

		getCellValidationMessage(months, field) {
			const key = this.cellKey(months, field)
			return this.cellValidationErrors[key] || null
		},

		getCellValidator(months, field) {
			const key = this.cellKey(months, field)
			const hasError = this.cellValidationErrors[key] !== undefined

			return {
				$dirty: hasError,
				$error: hasError,
				$invalid: hasError,
				$touch: () => {},
				$reset: () => {},
			}
		},

		clearCellValidationError(key) {
			if (this.cellValidationErrors[key]) {
				delete this.cellValidationErrors[key]
				this.cellValidationErrors = { ...this.cellValidationErrors }
			}
		},

		onCellEditComplete(event) {
			const { data, newValue, field } = event
			const rowIndex = this.table.indexOf(data)
			const key = this.cellKey(data.months, field)
			const errorMessage = this.cellError(rowIndex, field, Number(newValue))

			if (errorMessage) {
				this.cellValidationErrors = {
					...this.cellValidationErrors,
					[key]: errorMessage,
				}
				event.preventDefault()
				return
			}

			data[field] = newValue
			delete this.editStartedRed[key]
			this.clearCellValidationError(key)
			this.recheckCellErrors()
			this.pasteSummary = []
		},

		onCellEditCancel(event) {
			const { data, field } = event
			const rowIndex = this.table.findIndex((row) => row.months === data.months)
			const key = this.cellKey(data.months, field)
			const startedRed = this.editStartedRed[key]
			delete this.editStartedRed[key]
			const stored = startedRed && this.cellError(rowIndex, field, Number(this.table[rowIndex][field]))
			if (stored) {
				this.cellValidationErrors = { ...this.cellValidationErrors, [key]: stored }
			} else {
				this.clearCellValidationError(key)
			}
		},
	},
}
</script>

<style>
.contract-residual-values-table-modal {
	padding: 40px;
}

.contract-residual-values-table-modal__note {
	margin-top: 12px;
	color: var(--text-dark-muted);
}

.contract-residual-values-table-modal__cell {
	display: block;
	border-radius: 4px;
}

.contract-residual-values-table-modal__cell--error {
	outline: 1px solid var(--color-error);
}

.contract-residual-values-table-modal__paste-button {
	width: 100%;
}

.contract-residual-values-table-modal__paste-input {
	width: 100%;
	padding: 1.5rem 2.4rem;
	border: 1px solid var(--color-neutral-600);
	border-radius: 8px;
	font-family: inherit;
	font-size: 1.8rem;
	resize: vertical;
	background-color: var(--color-neutral-300);
	color: var(--color-secondary);
}

.contract-residual-values-table-modal__paste-error {
	color: var(--color-error);
}

.contract-residual-values-table-modal div.rv-matrix.rv-matrix--single.p-datatable .p-datatable-table {
	width: 580px;
}

.contract-residual-values-table-modal th.rv-tenor-h {
	width: 96px;
	padding: 11px 8px;
	text-align: center;
	border-bottom: 1px solid var(--color-neutral-700);

	& .p-datatable-column-header-content {
		justify-content: center;
	}
}

.contract-residual-values-table-modal td.rv-tenor-col {
	text-align: center;
}

.contract-residual-values-table-modal .rv-gh__label--split {
	display: flex;
	width: 100%;
	gap: 6px;
	white-space: normal;
	text-align: center;

	& > :first-child {
		flex: 1 1 60%;
	}

	& > :last-child {
		flex: 1 1 40%;
	}
}
</style>
